// ── Adapter Supabase do INotificationsRepository ──────────────────
// Única camada que conhece as tabelas `notifications` e
// `user_notification_prefs`. As linhas nascem dos triggers do Config/103;
// aqui só se lê, marca lido, descarta e configura preferência.
//
// A RLS já recorta por auth.uid() — os filtros por user_id abaixo são
// para o índice, não para segurança.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import type { Database, Json } from '@/types/database.types';
import type { NotificationEntry, NotificationPrefs } from '@/types/notification';
import type {
  INotificationsRepository,
  ListNotificationsQuery,
  SavePrefsData,
} from '@modules/notifications';

const requireSupabase = (): TypedSupabaseClient => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

export class SupabaseNotificationsRepository implements INotificationsRepository {
  listRecent = async ({
    userId, deliveredOnly = true, limit = 30,
  }: ListNotificationsQuery): Promise<NotificationEntry[]> => {
    const supabase = requireSupabase();
    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId);

    // Agendadas para o futuro não são "caixa de entrada" — só aparecem
    // depois que o dispatcher (Config/104) marca delivered_at.
    if (deliveredOnly) query = query.not('delivered_at', 'is', null);

    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data || []) as unknown as NotificationEntry[];
  };

  countUnread = async (userId: string): Promise<number> => {
    const supabase = requireSupabase();
    const { count, error } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('read_at', null)
      .not('delivered_at', 'is', null);
    if (error) throw error;
    return count || 0;
  };

  markRead = async (ids: string[]): Promise<void> => {
    const supabase = requireSupabase();
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .in('id', ids)
      .is('read_at', null);
    if (error) throw error;
  };

  markAllRead = async (userId: string): Promise<void> => {
    const supabase = requireSupabase();
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .is('read_at', null);
    if (error) throw error;
  };

  remove = async (id: string): Promise<void> => {
    const supabase = requireSupabase();
    const { error } = await supabase.from('notifications').delete().eq('id', id);
    if (error) throw error;
  };

  getPrefs = async (userId: string, workspaceId: string): Promise<NotificationPrefs | null> => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('user_notification_prefs')
      .select('*')
      .eq('user_id', userId)
      .eq('workspace_id', workspaceId)
      .maybeSingle();
    if (error) throw error;
    return (data as unknown as NotificationPrefs) || null;
  };

  savePrefs = async ({ userId, workspaceId, prefs }: SavePrefsData): Promise<NotificationPrefs> => {
    const supabase = requireSupabase();
    // Os campos jsonb chegam como tipos de domínio (interfaces sem index
    // signature) — o cast para Json é a tradução domínio→infra, e é aqui
    // que ela deve morar.
    const payload: Database['public']['Tables']['user_notification_prefs']['Insert'] = {
      user_id:      userId,
      workspace_id: workspaceId,
      ...(prefs.channels            !== undefined && { channels: prefs.channels as unknown as Json }),
      ...(prefs.activity_lead_times !== undefined && { activity_lead_times: prefs.activity_lead_times }),
      ...(prefs.meeting_lead_times  !== undefined && { meeting_lead_times: prefs.meeting_lead_times }),
      ...(prefs.kinds               !== undefined && { kinds: prefs.kinds as unknown as Json }),
      ...(prefs.quiet_hours         !== undefined && { quiet_hours: prefs.quiet_hours as unknown as Json }),
    };

    const { data, error } = await supabase
      .from('user_notification_prefs')
      .upsert(payload, { onConflict: 'user_id,workspace_id' })
      .select('*')
      .single();
    if (error) throw error;
    return data as unknown as NotificationPrefs;
  };
}
